import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { View, TextInput, StyleSheet, TouchableOpacity, ScrollView, Alert, Text, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Image } from 'expo-image';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { useTranslation } from 'react-i18next';
import { Theme, useThemedStyles, useTheme } from '../theme';

export interface InputBarHandle {
  appendText: (text: string) => void;
  focus: () => void;
}

interface Props {
  onSend?: (text: string, media?: { uri: string; mimeType: string }[]) => void;
  replyTarget?: { senderName?: string; text: string; avatarUrl?: string; images?: any[] } | null;
  quoteTarget?: { senderName?: string; text: string; avatarUrl?: string; images?: any[] } | null;
  onCancelTarget?: () => void;
  disableMedia?: boolean;
}

const InputBar = forwardRef<InputBarHandle, Props>(({ onSend, replyTarget, quoteTarget, onCancelTarget, disableMedia }, ref) => {
  const [text, setText] = useState('');
  const [selectedMedia, setSelectedMedia] = useState<{ uri: string; mimeType: string }[]>([]);
  const textInputRef = React.useRef<any>(null);
  const insets = useSafeAreaInsets();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useThemedStyles(createStyles);

  useImperativeHandle(ref, () => ({
    appendText: (newText: string) => {
      setText(prev => {
        if (prev.includes(newText)) return prev;
        return prev ? `${prev} ${newText}` : newText;
      });
      setTimeout(() => {
        textInputRef.current?.focus();
      }, 50);
    },
    focus: () => {
      textInputRef.current?.focus();
    }
  }));

  const pickImage = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images', 'videos'],
      allowsMultipleSelection: true,
      quality: 0.8,
    });

    if (!result.canceled) {
      const newMedia = result.assets.map(asset => ({
        uri: asset.uri,
        mimeType: asset.mimeType || (asset.type === 'video' ? 'video/mp4' : 'image/jpeg'),
      }));
      setSelectedMedia([...selectedMedia, ...newMedia]);
    }
  };

  const removeMedia = (index: number) => {
    const newMedia = [...selectedMedia];
    newMedia.splice(index, 1);
    setSelectedMedia(newMedia);
  };

  const handleSend = () => {
    if ((text.trim() || selectedMedia.length > 0) && onSend) {
      onSend(text, selectedMedia);
      setText('');
      setSelectedMedia([]);
    }
  };

  return (
    <View style={[styles.keyboardView, { paddingLeft: insets.left, paddingRight: insets.right }]}>
      {(replyTarget || quoteTarget) && (
        <View style={styles.targetPreview}>
          <View style={styles.targetContent}>
            <View style={styles.targetHeader}>
              {replyTarget ? (
                <Ionicons name="arrow-undo" size={14} color={theme.colors.primary} />
              ) : (
                <MaterialCommunityIcons name="format-quote-close" size={14} color={theme.colors.primary} />
              )}
              <Text style={styles.targetLabel}>
                {replyTarget ? t('inputBar.replyTo') : t('inputBar.quoteOf')}
              </Text>
            </View>
            <View style={styles.targetBody}>
              {((replyTarget?.avatarUrl || quoteTarget?.avatarUrl)) && (
                <Image 
                  source={{ uri: replyTarget?.avatarUrl || quoteTarget?.avatarUrl }} 
                  style={styles.targetAvatar} 
                  cachePolicy="memory-disk"
                />
              )}
              <View style={styles.targetTextContainer}>
                <Text style={styles.targetAuthor} numberOfLines={1}>
                  {replyTarget?.senderName || quoteTarget?.senderName}
                </Text>
                <Text style={styles.targetText} numberOfLines={1}>
                  {replyTarget?.text || quoteTarget?.text || (replyTarget?.images || quoteTarget?.images ? t('inputBar.images') : t('inputBar.media'))}
                </Text>
              </View>
            </View>
          </View>
          <TouchableOpacity onPress={onCancelTarget} style={styles.cancelTargetButton}>
            <Ionicons name="close-circle" size={24} color={theme.colors.textMuted} />
          </TouchableOpacity>
        </View>
      )}

      {selectedMedia.length > 0 && (
        <View style={styles.previewContainer}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {selectedMedia.map((img, index) => (
              <View key={index} style={styles.imagePreviewWrapper}>
                <Image 
                  source={{ uri: img.uri }} 
                  style={styles.imagePreview} 
                  cachePolicy="memory-disk"
                />
                <TouchableOpacity 
                  style={styles.removeButton} 
                  onPress={() => removeMedia(index)}
                >
                  <Ionicons name="close-circle" size={20} color="rgba(0,0,0,0.6)" />
                </TouchableOpacity>
              </View>
            ))}
          </ScrollView>
        </View>
      )}

      <View style={styles.container}>
        {!disableMedia && (
          <>
            <TouchableOpacity style={styles.iconButton}>
              <Ionicons name="add-outline" size={28} color={theme.colors.text} />
            </TouchableOpacity>
            <TouchableOpacity style={styles.iconButton} onPress={pickImage}>
              <Ionicons name="image-outline" size={28} color={theme.colors.text} />
            </TouchableOpacity>
          </>
        )}
        
        <View style={styles.inputContainer}>
          <TextInput
            style={styles.input}
            placeholder="メッセージを入力"
            placeholderTextColor={theme.colors.textMuted}
            ref={textInputRef}
            value={text}
            onChangeText={setText}
            multiline
          />
        </View>

        <TouchableOpacity style={styles.iconButton} onPress={handleSend}>
          <Ionicons name="send" size={24} color={(text.length > 0 || selectedMedia.length > 0) ? theme.colors.primary : theme.colors.textMuted} />
        </TouchableOpacity>
      </View>
    </View>
  );
});

const createStyles = (theme: Theme) => StyleSheet.create({
  keyboardView: {
    flexShrink: 0,
    backgroundColor: theme.colors.card,
    borderTopWidth: 0.5,
    borderTopColor: theme.colors.border,
  },
  previewContainer: {
    paddingTop: 8,
    paddingHorizontal: 12,
    backgroundColor: theme.colors.card,
  },
  targetPreview: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.cardSecondary,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  targetContent: {
    flex: 1,
  },
  targetHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  targetLabel: {
    fontSize: 11,
    fontWeight: 'bold',
    color: theme.colors.primary,
    marginLeft: 4,
  },
  targetBody: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  targetAvatar: {
    width: 24,
    height: 24,
    borderRadius: 12,
    marginRight: 8,
  },
  targetTextContainer: {
    flex: 1,
  },
  targetAuthor: {
    fontSize: 12,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  targetText: {
    fontSize: 12,
    color: theme.colors.textMuted,
  },
  cancelTargetButton: {
    padding: 4,
  },
  imagePreviewWrapper: {
    marginRight: 8,
    position: 'relative',
    marginBottom: 8,
  },
  imagePreview: {
    width: 60,
    height: 60,
    borderRadius: 8,
  },
  removeButton: {
    position: 'absolute',
    top: -5,
    right: -5,
    backgroundColor: 'white',
    borderRadius: 10,
  },
  container: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: 4,
    paddingVertical: 8,
    backgroundColor: theme.colors.card,
  },
  iconButton: {
    padding: 6,
  },
  inputContainer: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: theme.colors.input,
    borderRadius: 20,
    marginHorizontal: 4,
    paddingHorizontal: 12,
    alignItems: 'flex-end',
    minHeight: 40,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  input: {
    flex: 1,
    fontSize: 16,
    paddingVertical: 8,
    maxHeight: 100,
    color: theme.colors.text,
    ...Platform.select({
      web: { outlineStyle: 'none' } as any,
    }),
  },
  emojiButton: {
    paddingBottom: 8,
    paddingLeft: 4,
  },
});

export default InputBar;
